<script setup lang="ts">
import { computed, type PropType, reactive, ref, watch } from "vue";
import AdminUnits from "@/domains/home/services/nir/forms/components/widgets/AdminUnits.vue";
import Contact from "@/domains/home/services/nir/forms/components/widgets/Contact.vue";
import Country from "@/domains/home/services/nir/forms/components/widgets/Country.vue";
import Individuals from "@/domains/home/services/nir/forms/components/widgets/Individuals.vue";
import BeneficialOwners from "@/domains/home/services/nir/forms/components/widgets/BeneficialOwners.vue";
import NatureOfBusinessISIC from "@/domains/home/services/nir/forms/components/widgets/NatureOfBusinessISIC.vue";
import PostalAddress from "@/domains/home/services/nir/forms/components/widgets/PostalAddress.vue";
import { EntryField, FormFile } from "@/domains/home/services/registry/types";
import { FormDataSection, FormDefinition } from "@/domains/home/services/nir/configurations/types/forms.ts";
import {
  AdminUnitsSelection, FormBeneficialOwnerPerson,
  FormContact,
  FormCorporate,
  type FormPerson,
  IPostalAddress,
  ISICSelection
} from "@/domains/home/services/nir/forms/components/widgets/types";
import Corporates from "@/domains/home/services/nir/forms/components/widgets/Corporates.vue";
import PhoneInput from "@/components/PhoneInput/PhoneInput.vue";

const props = defineProps({
  formDefinition: Object as PropType<FormDefinition>,
  data: Object as PropType<EntryField[]>,
  active: Boolean,
})

const emit = defineEmits(['submit', 'upload', 'removeUpload', 'save'])

const attachments = ref<string[]>([])
const formData = reactive<EntryField[]>([])
const currentFormData = reactive<EntryField[]>([])
const formFile = ref<FormFile>({
  formId:'',
  attachmentId: '',
  file:null
})
const currentStep = ref(1)

// Populate formData from props.data for resume/prefill scenario
watch(() => props.data, (newData) => {
  if (newData && newData.length > 0) {
    formData.length = 0
    currentFormData.length = 0
    // Deep clone to avoid reference issues
    const clonedData = newData.map(item => ({
      fieldId: item.fieldId,
      fieldName: item.fieldName,
      value: item.value
    }))
    currentFormData.push(...clonedData)

  }
}, { immediate: true })

function save() {

  if (currentStep.value < orderedSteps.value.length) {
    if(props.active){
      const missing = currentFormData.filter(item => !formData.some(f => f.fieldId === item.fieldId))
      formData.push(...missing)
      let stepId = orderedSteps.value[currentStep.value - 1]?.id
      emit('save', formData, stepId)
      formData.length = 0
    }
    currentStep.value++
  }
}

function submit() {
  emit('submit')
}

function prevStep() {
  if (currentStep.value > 1) {
    currentStep.value--
  }
}

// Computed property to get sorted sections
const orderedSections = (sections:FormDataSection[]) => {
  if (!sections) return []
  return [...sections].sort((a, b) => a.position - b.position)
}

const orderedSteps = computed(() => {
  if (!props.formDefinition?.steps) return []
  return [...props.formDefinition.steps].sort((a, b) => a.position - b.position)
})

function getSortedFields(fields: any[]) {
  if (!fields) return []
  return [...fields].sort((a, b) => a.position - b.position)
}

function getSortedOptions(options: any[]) {
  if (!options || !Array.isArray(options)) return []
  return [...options].filter((opt) => opt.isActive).sort((a, b) => a.position - b.position)
}

function updateFormData(fieldId: string, fieldName: string, value: any) {
  const existingIndex = formData.findIndex((item) => item.fieldId === fieldId)

  if (existingIndex !== -1 && formData[existingIndex]) {
    formData[existingIndex].value = value
  } else {
    formData.push({
      fieldName,
      fieldId,
      value,
    })
  }
}

// Helper functions to get field values for prefilling
function getFieldValue(fieldId: string) {
  const entry = currentFormData.find(item => item.fieldId === fieldId)
  return entry?.value ?? ''
}

function getObjectFieldValue<T>(fieldId: string) {
  const entry = currentFormData.find(item => item.fieldId === fieldId)
  return entry?.value as T  || ref<T|undefined>().value
}

function isCheckboxChecked(fieldId: string, optionValue?: string) {
  const entry = formData.find(item => item.fieldId === fieldId)
  if (!entry) return false

  if (optionValue) {
    // Multiple checkboxes - check if value is in array
    return Array.isArray(entry.value) && entry.value.includes(optionValue)
  }
  // Single checkbox - return boolean value
  return !!entry.value
}

function handleFieldChange(event: Event, fieldId: string, fieldName: string) {
  const target = event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  updateFormData(fieldId, fieldName, target.value)
}

function handleCheckboxChange(
  event: Event,
  fieldId: string,
  fieldName: string,
  optionValue?: string,
) {
  const target = event.target as HTMLInputElement
  const existingEntry = formData.find((item) => item.fieldId === fieldId)

  if (optionValue) {
    // Multiple checkboxes - store as array
    let values: string[] = Array.isArray(existingEntry?.value) ? existingEntry.value : []
    if (target.checked) {
      values.push(optionValue)
    } else {
      values = values.filter((v) => v !== optionValue)
    }
    updateFormData(fieldId, fieldName, values)
  } else {
    // Single checkbox - store as boolean
    updateFormData(fieldId, fieldName, target.checked)
  }
}

function handleFileChange(event: Event, attachmentId: string) {
  attachments.value.push(attachmentId)
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    formFile.value.formId = props.formDefinition?.id || "";
    formFile.value.attachmentId = attachmentId;
    formFile.value.file = target.files[0]
    uploadFile()
  }
}

// Handler for custom component value changes (AdminUnitsView, ISIC, Contact, Country, etc.)
function handleComponentChange(value: any, fieldId: string, fieldName: string) {
  updateFormData(fieldId, fieldName, value)
}

function uploadFile() {
  if(props.active){
    emit('upload', formFile.value)
  }
}

function removeFile(event: Event, attachmentId:string) {
  let idx = attachments.value.indexOf(attachmentId)
  attachments.value.splice(idx , 1)

  const target = event.target as HTMLInputElement
  if(props.active){
    emit('removeUpload', attachmentId)
    target.remove()
  }
}

</script>

<template>
  <div class="flex flex-col border border-gray-200 rounded-xl overflow-y-auto px-6 py-4" :class="!active ? 'bg-gray-50' : ''">
    <!-- Form Header -->
    <div v-if="props.formDefinition" class="mb-6 p-6 border-b-2 border-gray-300">
      <h2 class="text-3xl font-bold text-gray-900">{{ props.formDefinition.name }}</h2>
      <p class="text-gray-600 mt-2">{{ props.formDefinition.description }}</p>
    </div>

    <!-- Step Indicator -->
    <div class="mb-8">
      <div class="flex items-center justify-center">
        <template v-for="(step, index) in orderedSteps" :key="step.position">
          <!-- Step Circle -->
          <div class="flex items-center">
            <div
              :class="[
                'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all',
                currentStep >= (step.position + 1)
                  ? 'bg-primary border-primary text-white'
                  : 'bg-white border-gray-300 text-gray-500'
              ]"
            >
              <i class="fas fa-dot-circle"></i>
            </div>
            <span
              :class="[
                'ml-2 text-sm font-medium',
                currentStep >= step.position + 1 ? 'text-primary' : 'text-gray-500'
              ]">
              {{ step.name }}
            </span>
          </div>
          <!-- Connector Line -->
          <div
            v-if="index < orderedSteps.length - 1"
            :class="[
              'w-16 h-1 mx-4 rounded',
              currentStep > (step.position + 1) ? 'bg-primary' : 'bg-gray-300'
            ]"
          ></div>
        </template>
      </div>
      <p class="text-center text-sm text-gray-500 mt-3">
        Step {{ currentStep }} of {{ orderedSteps.length }}
      </p>
    </div>

    <!-- Step 1: Form Fields -->
    <div class="flex flex-col" v-if="orderedSteps.length > 0">
      <div v-for="(step, idx) in orderedSteps" v-show="currentStep === step.position + 1" :key="idx" class="flex flex-col" id="form">
        <!-- Form Sections -->
        <div v-if="orderedSections(step.sections).length > 0" class="space-y-8">
          <div v-for="(section, sectionIndex) in orderedSections(step.sections)" :key="section.id" class="space-y-4">
            <!-- Section Title -->
            <div class="border-l-4 border-primary-600 pl-4 py-2">
              <h3 class="text-xl font-semibold text-gray-800">
                {{ sectionIndex + 1 }}. {{ section.name }}
              </h3>
              <p v-if="section.description" class="text-gray-600 text-sm mt-1">
                {{ section.description }}
              </p>
            </div>

            <!-- Section Fields -->
            <div v-if="section.fields && section.fields.length > 0" class="pl-6 space-y-5">
              <div v-for="fieldData in getSortedFields(section.fields)"
                  :key="fieldData.field.id"
                  v-show="fieldData.field.isActive"
                  class="space-y-2">
                <label class="block">
                <span class="text-sm font-medium text-gray-700">
                  <label v-if="fieldData.label">{{ fieldData.label }}</label>
                  <label v-else>{{ fieldData.field.label }}</label>
                  <i v-if="fieldData.field.isMandatory" class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
                </span>
                  <!-- Text Input -->
                  <input
                      v-if="
                  fieldData.field.type === 'text' ||
                  fieldData.field.type === 'email' ||
                  fieldData.field.type === 'number'"
                      :type="fieldData.field.type.toLowerCase()"
                      :value="getFieldValue(fieldData.field.id)"
                      :placeholder="fieldData.field.placeholder ? fieldData.field.placeholder : `Enter ${fieldData.field.label.toLowerCase()}`"
                      :required="fieldData.field.isMandatory"
                      :disabled="!fieldData.field.isActive"
                      @input="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                      class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />

                  <!-- Textarea -->
                  <textarea
                      v-else-if="
                  fieldData.field.type === 'textarea' || fieldData.field.type === 'long_text'"
                      :value="getFieldValue(fieldData.field.id) as string"
                      :placeholder="fieldData.field.placeholder ? fieldData.field.placeholder : `Enter ${fieldData.field.label.toLowerCase()}`"
                      :required="fieldData.field.isMandatory"
                      :disabled="!fieldData.field.isActive"
                      @input="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                      rows="3"
                      class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  ></textarea>

                  <!-- Select Dropdown -->
                  <select
                      v-else-if="fieldData.field.type === 'select' || fieldData.field.type === 'dropdown'"
                      :value="getFieldValue(fieldData.field.id)"
                      :required="fieldData.field.isMandatory"
                      :disabled="!fieldData.field.isActive"
                      @change="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                      class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select {{ fieldData.field.label.toLowerCase() }}</option>
                    <option
                        v-for="option in getSortedOptions(fieldData.field.options)"
                        :key="option.id"
                        :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>

                  <!-- Date Input -->
                  <input
                      v-else-if="fieldData.field.type === 'date'"
                      type="date"
                      :value="getFieldValue(fieldData.field.id)"
                      :required="fieldData.field.isMandatory"
                      :disabled="!fieldData.field.isActive"
                      @change="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                      class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />

                  <!-- Checkbox -->
                  <div v-else-if="fieldData.field.type === 'checkbox' || fieldData.field.type === 'boolean'"
                      class="mt-2 space-y-2">
                    <div v-if="fieldData.field.options && fieldData.field.options.length > 0">
                      <label
                          v-for="option in getSortedOptions(fieldData.field.options)"
                          :key="option.id"
                          class="flex items-center">
                        <input
                            type="checkbox"
                            :value="option.value"
                            :checked="isCheckboxChecked(fieldData.field.id, option.value)"
                            :disabled="!fieldData.field.isActive"
                            @change="
                        handleCheckboxChange(
                          $event,
                          fieldData.field.id,
                          fieldData.field.name,
                          option.value,
                        )
                      "
                            class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <span class="ml-2 text-sm text-gray-600">{{ option.label }}</span>
                      </label>
                    </div>
                    <label v-else class="inline-flex items-center">
                      <input
                          type="checkbox"
                          :checked="isCheckboxChecked(fieldData.field.id)"
                          :required="fieldData.field.isMandatory"
                          :disabled="!fieldData.field.isActive"
                          @change="handleCheckboxChange($event, fieldData.field.id, fieldData.field.name)"
                          class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                      <span class="ml-2 text-sm text-gray-600">Check this box</span>
                    </label>
                  </div>

                  <!-- Radio Buttons -->
                  <div v-else-if="fieldData.field.type === 'radio'" class="mt-2 space-y-2">
                    <div v-if="fieldData.field.options && fieldData.field.options.length > 0">
                      <label
                          v-for="option in getSortedOptions(fieldData.field.options)"
                          :key="option.id"
                          class="flex items-center"
                      >
                        <input
                            type="radio"
                            :name="`radio-${fieldData.field.id}`"
                            :value="option.value"
                            :checked="getFieldValue(fieldData.field.id) === option.value"
                            :required="fieldData.field.isMandatory"
                            :disabled="!fieldData.field.isActive"
                            @change="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                            class="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <span class="ml-2 text-sm text-gray-600">{{ option.label }}</span>
                      </label>
                    </div>
                    <div v-else>
                      <label class="inline-flex items-center">
                        <input
                            type="radio"
                            :name="`radio-${fieldData.field.id}`"
                            :required="fieldData.field.isMandatory"
                            :disabled="!fieldData.field.isActive"
                            @change="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                            class="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <span class="ml-2 text-sm text-gray-600">Option 1</span>
                      </label>
                      <label class="inline-flex items-center ml-4">
                        <input
                            type="radio"
                            :name="`radio-${fieldData.field.id}`"
                            :disabled="!fieldData.field.isActive"
                            @change="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                            class="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <span class="ml-2 text-sm text-gray-600">Option 2</span>
                      </label>
                    </div>
                  </div>

                  <PostalAddress
                      v-else-if="fieldData.field.type === 'postal-address'"
                      :data="getObjectFieldValue<IPostalAddress>(fieldData.field.id)"
                      :active="active"
                      @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
                  />
                  <AdminUnits
                      v-else-if="fieldData.field.type === 'admin-units'"
                      :data="getObjectFieldValue<AdminUnitsSelection>(fieldData.field.id)"
                      :active="active"
                      @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
                  />
                  <NatureOfBusinessISIC
                      v-else-if="fieldData.field.type === 'isic'"
                      :data="getObjectFieldValue<ISICSelection>(fieldData.field.id)"
                      :active="active"
                      @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
                  />
                  <Contact
                      v-else-if="fieldData.field.type === 'contact'"
                      :cardinality="fieldData.field.cardinality"
                      :data="getObjectFieldValue<FormContact[]>(fieldData.field.id)"
                      :active="active"
                      @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
                  />
                  <PhoneInput
                      v-else-if="fieldData.field.type === 'tel'"
                      :value="getFieldValue(fieldData.field.id)"
                      :required="fieldData.field.isMandatory"
                      :disabled="!fieldData.field.isActive"
                      :label="fieldData.field.label"
                      defaultCountry="+256"
                      placeholder="Enter phone number"
                      size="sm"
                  />
                  <Country
                      v-else-if="fieldData.field.type === 'country'"
                      :data="getFieldValue(fieldData.field.id) as string"
                      :active="active"
                      @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
                  />
                  <Individuals
                      v-else-if="fieldData.field.type === 'individuals'"
                      :cardinality="fieldData.field.cardinality"
                      :data="getObjectFieldValue<FormPerson[]>(fieldData.field.id)"
                      :active="active"
                      @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
                  />
                  <Corporates
                      v-else-if="fieldData.field.type === 'corporates'"
                      :cardinality="fieldData.field.cardinality"
                      :data="getObjectFieldValue<FormCorporate[]>(fieldData.field.id)"
                      :active="active"
                      @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
                  />
                  <BeneficialOwners
                      v-else-if="fieldData.field.type === 'beneficial-owners'"
                      :cardinality="fieldData.field.cardinality"
                      :data="getObjectFieldValue<FormBeneficialOwnerPerson[]>(fieldData.field.id)"
                      :active="active"
                      @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
                  />
                  <!-- Default/Generic Input -->
                  <input
                      v-else
                      type="text"
                      :value="getFieldValue(fieldData.field.id)"
                      :placeholder="`Enter ${fieldData.field.label.toLowerCase()}`"
                      :required="fieldData.field.isMandatory"
                      :disabled="!fieldData.field.isActive"
                      @input="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                      class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />

                  <!-- Field Info -->
                  <div class="mt-1 flex items-center gap-3 text-xs">
                <span
                    v-if="!fieldData.field.isActive"
                    class="text-orange-600 flex items-center gap-1"
                >
                  <i class="fa-solid fa-exclamation-triangle"></i>
                  Inactive field
                </span>
                  </div>
                </label>
              </div>
            </div>

            <!-- Empty Section -->
            <div v-else class="pl-6 text-gray-500 italic">No fields in this section</div>
          </div>
        </div>
        <!-- Empty State -->
        <div v-else class="text-center py-12">
          <i class="fa-solid fa-folder-open text-5xl text-gray-300 mb-4"></i>
          <p class="text-gray-500">No sections created yet. Add sections to preview your form.</p>
        </div>
      </div>
      <div v-show="currentStep === orderedSteps.length" class="mt-8" id="attachments">
        <div class="border-l-4 border-primary-600 pl-4 py-2 mb-4">
          <h3 class="text-xl font-semibold text-gray-800">
            <i class="fa-solid fa-paperclip mr-2"></i>
            Attachments
          </h3>
          <p class="text-gray-600 text-sm mt-1">Upload the required documents</p>
        </div>

        <div class="pl-6 space-y-5" v-if="props.formDefinition">
          <div
              v-for="attachment in props.formDefinition?.attachments"
              :key="attachment.id"
              :class="{'border border-red-200 p-2 rounded-lg' : attachment.isMandatory,
           'border border-gray-50 p-2 rounded-lg' : !attachment.isMandatory}"
              class="space-y-2">
            <div class="block">
              <label class="text-sm font-medium text-gray-700">
                {{ attachment.label }}
                <i v-if="attachment.isMandatory" class="text-red-500 fas fa-asterisk text-[10px]"></i>
              </label>
              <p v-if="attachment.description" class="text-xs text-gray-500 mt-1">{{ attachment.description }}</p>
              <p class="text-xs text-red-500 mt-1">Accepts only {{ attachment.fileType }}</p>
              <div class="flex gap-2 my-1">
                <i class="fa fa-paperclip my-auto text-primary"></i>
                <input type="file" :required="attachment.isMandatory" :disabled="!active"
                       @change="handleFileChange($event, attachment.id)"
                       class="block w-full my-auto text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"/>
                <button class="btn-sm-danger" v-if="attachments.includes(attachment.id)" type="button" @click="removeFile($event, attachment.id)"> <i class="fas fa-times-circle"></i></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-12">
      <i class="fa-solid fa-folder-open text-5xl text-gray-300 mb-4"></i>
      <p class="text-gray-500">No steps created yet. Add steps to preview your form.</p>
    </div>

    <!-- Step 2: Attachments -->


    <!-- Navigation Buttons -->
    <div class="mt-8 pt-6 border-t-2 border-gray-100">
      <div class="flex justify-between items-center">
        <!-- Previous Button -->
        <button
          v-if="currentStep > 1"
          @click="prevStep"
          type="button"
          class="flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 transition"
        >
          <i class="fa-solid fa-arrow-left"></i>
          Previous
        </button>
        <div v-else></div>
        <!-- Next / Submit Button -->
        <div class="flex gap-3">
          <button
            v-if="currentStep < orderedSteps.length"
            @click="save"
            type="button"
            class="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-md font-medium hover:bg-primary-700 transition">
            <label v-if="active">Save & Continue</label>
            <label v-else>Next</label>
            <i class="fa-solid fa-arrow-right"></i>
          </button>
          <button
            v-if="currentStep === orderedSteps.length && active"
            @click="submit"
            type="button"
            class="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-md font-semibold hover:bg-green-700 transition">
            Submit <i class="fa-solid fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
